import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StarRating } from "@/components/StarRating";
import { MapView } from "@/components/MapView";
import { DispatchModal } from "@/components/DispatchModal";
import { CATEGORY_META, TIER_STYLE } from "@/lib/constants";
import { Phone, MapPin, Clock, BadgeCheck, Zap, ArrowLeft, Crown, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ProviderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [dispatch, setDispatch] = useState(false);
  const [form, setForm] = useState({ customer_name: "", rating: 5, comment: "" });
  const { user, openAuth } = useAuth();
  const [eligibility, setEligibility] = useState(null);

  const load = async () => {
    const [p, r] = await Promise.all([api.get(`/providers/${id}`), api.get(`/providers/${id}/reviews`)]);
    setProvider(p.data); setReviews(r.data);
  };
  const loadEligibility = async () => {
    if (!user) { setEligibility(null); return; }
    try { const { data } = await api.get(`/providers/${id}/can-review`); setEligibility(data); }
    catch { setEligibility(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { loadEligibility(); /* eslint-disable-next-line */ }, [id, user, dispatch]);

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/providers/${id}/reviews`, form);
      toast.success("Review posted!");
      setForm({ customer_name: "", rating: 5, comment: "" });
      load(); loadEligibility();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!provider) return <div className="text-center py-20 text-slate-500">Loading...</div>;
  const meta = CATEGORY_META[provider.category] || CATEGORY_META.mechanics;
  const tier = provider.tier || "free";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-400 hover:text-white text-sm mb-4">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/70">
        <div className="h-48 sm:h-64 relative">
          {provider.image_url ? (
            <img src={provider.image_url} alt={provider.business_name} className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
              <meta.Icon size={64} className="text-slate-700" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
            <div>
              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${TIER_STYLE[tier]}`}>
                {tier === "gold" && <Crown size={11} className="inline mr-1" />} {tier}
              </span>
              <h1 className="font-head font-black text-3xl sm:text-4xl text-slate-50 mt-2 flex items-center gap-2">
                {provider.business_name}
                {provider.verified && <BadgeCheck size={24} className="text-blue-400" />}
              </h1>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
            <span className="flex items-center gap-1"><meta.Icon size={15} className="text-amber-500" /> {meta.name}</span>
            <span className="flex items-center gap-1"><MapPin size={15} className="text-amber-500" /> {provider.city}</span>
            <span className="flex items-center gap-1"><Clock size={15} className="text-amber-500" /> ~{provider.response_time_mins} min response</span>
            <span className="flex items-center gap-1"><StarRating value={provider.rating} size={15} /> {provider.rating || "New"} ({provider.review_count})</span>
          </div>

          {provider.description && <p className="text-slate-300 mt-4">{provider.description}</p>}

          {provider.services?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {provider.services.map((s) => (
                <span key={s} className="text-xs bg-slate-800 text-slate-200 px-3 py-1 rounded-full">{s}</span>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <a href={`tel:${provider.phone}`} className="flex-1">
              <Button variant="outline" className="w-full border-slate-700 bg-slate-800/50 hover:bg-slate-700 text-slate-100 rounded-xl h-12">
                <Phone size={17} className="mr-2" /> Call {provider.phone}
              </Button>
            </a>
            <Button data-testid="provider-detail-request-callout-button" onClick={() => setDispatch(true)} className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl h-12">
              <Zap size={17} className="mr-2" /> Request Call-out
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div>
          <h2 className="font-head font-bold text-xl text-slate-50 mb-3">Location</h2>
          <MapView providers={[provider]} height="h-[300px]" />
        </div>
        <div>
          <h2 className="font-head font-bold text-xl text-slate-50 mb-3">Reviews ({reviews.length})</h2>
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
            {reviews.length === 0 && <p className="text-slate-500 text-sm">No reviews yet. Be the first!</p>}
            {reviews.map((r) => (
              <div key={r.id} className="bg-slate-900/70 border border-slate-800 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-100 text-sm">{r.customer_name}</span>
                  <StarRating value={r.rating} size={12} />
                </div>
                {r.comment && <p className="text-slate-400 text-sm mt-1">{r.comment}</p>}
              </div>
            ))}
          </div>

          {!user && (
            <div data-testid="review-login-prompt" className="mt-4 bg-slate-900/70 border border-slate-800 rounded-xl p-4 text-sm text-slate-300">
              <p>Sign in and request a call-out to leave a review.</p>
              <Button data-testid="review-login-button" onClick={() => openAuth("login")} className="mt-3 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl w-full">Sign in</Button>
            </div>
          )}
          {user && eligibility && !eligibility.can_review && (
            <p data-testid="review-ineligible-message" className="mt-4 bg-slate-900/70 border border-slate-800 rounded-xl p-4 text-sm text-slate-400">{eligibility.reason}</p>
          )}
          {user && eligibility?.can_review && (
          <form data-testid="review-form" onSubmit={submitReview} className="mt-4 bg-slate-900/70 border border-slate-800 rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold text-slate-200">Leave a review</p>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              className="bg-slate-800 border-slate-700" placeholder={`Display name (default: ${user.name})`} data-testid="review-name-input" />
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })}>
                  <Star size={22} className={i <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-600"} />
                </button>
              ))}
            </div>
            <Textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
              className="bg-slate-800 border-slate-700" rows={2} placeholder="How was the service?" />
            <Button type="submit" data-testid="review-submit-button" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl w-full">Post review</Button>
          </form>
          )}
        </div>
      </div>

      <DispatchModal provider={dispatch ? provider : null} open={dispatch} onClose={() => setDispatch(false)} />
    </div>
  );
}
